/**
 * ALTIORA · Web Push subscriber (auto-mounted).
 *
 * On mount, if the browser supports Push API, ask permission gracefully
 * (delayed 5s to avoid the login-time popup) and post the subscription
 * to /api/web_push/subscribe.
 *
 * The service worker is served from /sw-push.js (public/).
 */
import { useEffect } from "react";
import { api } from "@/lib/api";

const REG_KEY = "altiora_push_registered";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function WebPushSubscriber() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
    // Only ask once per browser (persist across reloads)
    if (localStorage.getItem(REG_KEY) === "1") return;

    let cancelled = false;
    const run = async () => {
      try {
        // Do NOT ask permission automatically — wait for a user gesture.
        // We only register the SW; the subscription happens when
        // Notification.permission is already 'granted' (e.g. after the
        // user clicks the header button we expose in the future).
        if (Notification.permission !== "granted") return;
        const reg = await navigator.serviceWorker.register("/sw-push.js");
        const { data } = await api.get("/web_push/public_key");
        const applicationServerKey = urlBase64ToUint8Array(data.public_key);
        const sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey,
        });
        const json = sub.toJSON();
        await api.post("/web_push/subscribe", {
          endpoint: json.endpoint,
          keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
        });
        if (!cancelled) localStorage.setItem(REG_KEY, "1");
      } catch (err) {
        // Silent — push is a progressive enhancement
        // eslint-disable-next-line no-console
        console.debug("WebPushSubscriber: skipped —", err?.message);
      }
    };
    const id = setTimeout(run, 4000);
    return () => { cancelled = true; clearTimeout(id); };
  }, []);
  return null;
}

/** Utility button — asks permission then triggers subscription flow. */
export async function requestPushPermission() {
  if (!("Notification" in window)) return false;
  if (Notification.permission === "granted") return true;
  if (Notification.permission === "denied") return false;
  const result = await Notification.requestPermission();
  return result === "granted";
}
